import { toastr } from 'react-redux-toastr';
import { api } from '../../../../../config';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/svg+xml'];

/**
 * Extracts an image from a paste ClipboardEvent, uploads it to the server,
 * and returns the fileName from the server response.
 *
 * @param {ClipboardEvent} e
 * @param {string} uploadUrl - e.g. '/uploadItemImage'
 * @returns {Promise<string|null>} fileName if successful, null if no image in clipboard
 * @throws {Error} if upload fails
 */
export async function uploadPastedImage(e, uploadUrl) {
	const clipboardItems = e.clipboardData && e.clipboardData.items;
	if (!clipboardItems) return null;

	// Find first image item in clipboard
	let imageFile = null;
	for (let i = 0; i < clipboardItems.length; i++) {
		if (clipboardItems[i].type.startsWith('image/')) {
			imageFile = clipboardItems[i].getAsFile();
			break;
		}
	}

	if (!imageFile) return null;

	// Validate file type
	if (!ALLOWED_TYPES.includes(imageFile.type)) {
		toastr.error('Error!', 'Please paste a PNG, JPG or JPEG image.');
		return null;
	}

	// Give the pasted blob a proper filename
	const ext = imageFile.type === 'image/png' ? 'png' : 'jpg';
	const namedFile = new File([imageFile], `paste-${Date.now()}.${ext}`, { type: imageFile.type });

	const formData = new FormData();
	formData.append('file', namedFile);

	// Use XHR (same as Dropzone) — avoids CORS preflight conflict
	// that occurs when using fetch() with credentials + wildcard origin
	const result = await new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', api.apiEndpoint + uploadUrl);
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				try {
					resolve(JSON.parse(xhr.responseText));
				} catch (parseErr) {
					reject(new Error('Invalid JSON response from server'));
				}
			} else {
				reject(new Error(`Upload failed with status ${xhr.status}`));
			}
		};
		xhr.onerror = () => reject(new Error('Network error during upload'));
		xhr.send(formData);
	});

	if (result && result.fileName) {
		return result.fileName;
	}

	throw new Error('Server did not return a fileName');
}
